/**
 * ประวัติ: every decided row of both tables (never audit_logs), newest first,
 * as old → new where an earlier approved value for the same key exists.
 */
import { dataDateOf } from '@/services/dataDate';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CprTargetRow, PlanRow, ProposalStatus } from '@/services/targets/targetRules';
import { keyPlatformLabel, COST_LABEL, money, OBJECTIVE_LABEL, STATUS_LABEL, whoLabel } from './targetUi';

interface Props {
  plans: readonly PlanRow[];
  targets: readonly CprTargetRow[];
  me: string | null;
}

interface Entry {
  id: string;
  kindLabel: string;
  keyLabel: string;
  key: string;
  value: number;
  currency: string;
  status: ProposalStatus;
  at: string;
  proposedAt: string;
  decidedAt: string | null;
  proposedBy: string;
  decidedBy: string | null;
  reason: string | null;
}

const PAGE = 50;

const planEntry = (t: TFunction, r: PlanRow): Entry => ({
  id: r.id, kindLabel: t('targets.kind.plan'), keyLabel: `${r.month.slice(0, 7)} · ${keyPlatformLabel(t, r.platform)}`,
  key: `plan|${r.month}|${r.currency}|${r.platform ?? ''}`, value: r.amount, currency: r.currency, status: r.status,
  at: r.decidedAt ?? r.proposedAt, proposedAt: r.proposedAt, decidedAt: r.decidedAt, proposedBy: r.proposedBy, decidedBy: r.decidedBy, reason: r.overrideReason ?? r.rejectReason,
});

const targetEntry = (t: TFunction, r: CprTargetRow): Entry => ({
  id: r.id, kindLabel: t('targets.kind.target'),
  keyLabel: `${OBJECTIVE_LABEL[r.objective](t)} (${COST_LABEL[r.metric](t)}) · ${keyPlatformLabel(t, r.platform)} · ${t('targets.cpr.effectiveFrom')} ${r.effectiveFrom}`,
  key: `target|${r.objective}|${r.metric}|${r.currency}|${r.platform ?? ''}`, value: r.target, currency: r.currency, status: r.status,
  at: r.decidedAt ?? r.proposedAt, proposedAt: r.proposedAt, decidedAt: r.decidedAt, proposedBy: r.proposedBy, decidedBy: r.decidedBy, reason: r.overrideReason ?? r.rejectReason,
});

/**
 * Was this row ever the approved value? `superseded` covers two things in
 * migration 118: an approved row replaced by a newer approval (decided_at set)
 * and a proposal replaced by a newer proposal (never decided, decided_at NULL).
 * Only the first was ever in effect.
 */
const everApproved = (e: Entry): boolean => e.status === 'approved' || (e.status === 'superseded' && e.decidedAt !== null);

/**
 * The approved value of the same key proposed before this entry; null = first of
 * its key. Ordered by proposedAt: a superseded row is decided (superseded) at the
 * same instant its successor is approved, so decision times cannot order them.
 */
const previousOf = (all: readonly Entry[], e: Entry): Entry | null =>
  all.filter((o) => o.key === e.key && o.id !== e.id && everApproved(o) && o.proposedAt < e.proposedAt)
    .reduce<Entry | null>((best, o) => (best === null || o.proposedAt > best.proposedAt ? o : best), null);

export const TargetHistory: React.FC<Props> = ({ plans, targets, me }) => {
  const { t } = useTranslation();
  const [shown, setShown] = useState(PAGE);
  const entries = [...plans.map((r) => planEntry(t, r)), ...targets.map((r) => targetEntry(t, r))]
    .filter((e) => e.status !== 'proposed')
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6">
      <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('targets.history.heading')}</h2>
      {entries.length === 0 && <p className="text-sm text-neutral-700">{t('targets.history.empty')}</p>}
      <ul>
        {entries.slice(0, shown).map((e) => {
          const prev = previousOf(entries, e);
          return (
            <li key={e.id} className="py-2 border-t border-neutral-100 text-sm">
              <p className="text-ink-900">
                <span className="font-medium">{e.kindLabel}</span> · {e.keyLabel} · {STATUS_LABEL[e.status](t)}
              </p>
              <p className="text-ink-900">
                {prev ? `${money(prev.value, prev.currency)} → ` : ''}{money(e.value, e.currency)}
              </p>
              <p className="text-xs text-neutral-700">
                {dataDateOf(e.at)} · {t('targets.history.proposedBy', { who: whoLabel(t, e.proposedBy, me) })}
                {e.decidedBy && <> · {t('targets.history.decidedBy', { who: whoLabel(t, e.decidedBy, me) })}</>}
                {e.reason && <> · {e.reason}</>}
              </p>
            </li>
          );
        })}
      </ul>
      {entries.length > shown && (
        <button type="button" className="mt-2 text-xs font-medium underline" onClick={() => setShown((n) => n + PAGE)}>
          {t('targets.history.more')}
        </button>
      )}
    </section>
  );
};
